import useAsyncControl from '@onepercentio/one-ui/hooks/useAsyncControl'
import { FlutterCallHandler } from 'augmentation'
import { signInWithCustomToken } from 'core/modules/firebase/service/accounts'
import { debounce } from 'lodash'
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react'

export enum IconLocation {
  LEFT,
  RIGHT,
}
export type BridgeActions = 'clickedOnControl' | 'authChanged'
export type BridgeActionsParams = {
  clickedOnControl: [location: keyof typeof IconLocation]
}
export type BridgeEvents =
  | 'updateTitle'
  | 'updateControl'
  | 'profileData'
  | 'login'
  | 'getJWTToken'
  | 'setThemeColor'
  | 'openSupport'
  | 'openKnowMore'
  | 'openTermsAndCondition'
  | 'exitWebview'
  | 'shareLink'
export type BridgeEventProps = {
  updateTitle: {
    title: string
  }
  updateControl: {
    position: keyof typeof IconLocation
    active: boolean
  }
  shareLink: { link: string }
  setThemeColor: { background: string; foreground: string }
}
export type BridgeEventReturn = {
  profileData: {
    profilePicture: string
    level: string
  } | null
  getJWTToken: string
  login: null | false
}
export type PickBridgeEvent<
  E extends BridgeEvents,
  O extends BridgeEventProps | BridgeEventReturn
> = E extends keyof O ? O[E] : void

const RarumInterfaceListeners: { [A in BridgeActions]?: any[] } = {}
window.RarumInterface = new Proxy(
  {},
  {
    get(_, action) {
      return (props: any) => {
        const listeners = (RarumInterfaceListeners as any)[action] || []
        for (let listener of listeners) listener(props)
      }
    },
  }
) as any

function integrationModeInfo() {
  const integrationMode = (() => {
    if (window.flutter_inappwebview) return 'flutter'
    return null
  })()
  const isIntegrationMode = !(integrationMode === null)

  const eventHandler = (() => {
    switch (integrationMode) {
      case 'flutter':
        return callToFlutterBridge
      default:
        return ((e: BridgeEvents) => () =>
          Promise.resolve().then(
            () => {}
          )) as unknown as typeof callToFlutterBridge
    }
  })()
  function callToFlutterBridge<E extends BridgeEvents>(event: E) {
    return (
      props: E extends keyof BridgeEventProps ? BridgeEventProps[E] : void
    ): Promise<Awaited<ReturnType<FlutterCallHandler<E>>>> => {
      return window.flutter_inappwebview
        .callHandler('rarumFlutterHandler', {
          type: event,
          ...(props as any),
        })
        .then((ret: any) => {
          return ret
        })
    }
  }
  return {
    eventHandler,
    isIntegrationMode,
    integrationMode,
  }
}

const WebviewIntegrationContext = createContext<{
  control: ReturnType<typeof useAsyncControl>
  checkAuthentication: () => Promise<boolean>
}>(null as any)

export function WebviewIntegrationProvider({ children }: PropsWithChildren) {
  const { eventHandler } = integrationModeInfo()
  const control = useAsyncControl()
  const getJWTToken = eventHandler('getJWTToken')
  const checkAuthentication = useCallback(() => {
    return getJWTToken().then((jwt) => {
      if (jwt)
        return signInWithCustomToken(jwt)
          .then(() => true)
          .catch(() => false)
      return false
    })
  }, [])
  return (
    <WebviewIntegrationContext.Provider
      value={{
        checkAuthentication: () => control.process(() => checkAuthentication()),
        control,
      }}>
      {children}
    </WebviewIntegrationContext.Provider>
  )
}

export function useWebviewIntegration() {
  const { eventHandler, integrationMode, isIntegrationMode } =
    integrationModeInfo()
  const { checkAuthentication, control } = useContext(WebviewIntegrationContext)

  const useOnAction = <B extends BridgeActions | 'onBack' | 'onExit'>(
    event: B,
    callback: (
      ...params: B extends keyof BridgeActionsParams
        ? BridgeActionsParams[B]
        : []
    ) => void,
    deps: any[]
  ) => {
    useEffect(() => {
      function registerCallback<E extends BridgeActions>(
        k: E,
        cb: (
          ...params: E extends keyof BridgeActionsParams
            ? BridgeActionsParams[E]
            : []
        ) => void
      ) {
        if (!RarumInterfaceListeners[k]) RarumInterfaceListeners[k] = []
        RarumInterfaceListeners[k].push(cb)
        return () => {
          RarumInterfaceListeners[k] = RarumInterfaceListeners[k]!.filter(
            (registeredCb) => registeredCb !== cb
          )
        }
      }
      switch (event) {
        case 'onBack':
          return registerCallback(
            'clickedOnControl',
            debounce(
              (l) => {
                if (l === 'LEFT') (callback as any)()
              },
              500,
              { leading: true, trailing: false }
            )
          )
        case 'onExit':
          return registerCallback(
            'clickedOnControl',
            debounce(
              (l) => {
                if (l === 'RIGHT') (callback as any)()
              },
              500,
              { leading: true, trailing: false }
            )
          )
        default:
          return registerCallback(event, callback)
      }
    }, [event, ...deps])
  }

  const getJWTToken = eventHandler('getJWTToken')

  return {
    updateTitle: eventHandler('updateTitle'),
    getProfileData: eventHandler('profileData'),
    updateControl: eventHandler('updateControl'),
    openKnowMore: eventHandler('openKnowMore'),
    openSupport: eventHandler('openSupport'),
    openTermsAndCondition: eventHandler('openTermsAndCondition'),
    login: eventHandler('login'),
    setThemeColor: eventHandler('setThemeColor'),
    getJWTToken,
    exitWebview: eventHandler('exitWebview'),
    shareLink: eventHandler('shareLink'),
    integrationMode,
    on: useOnAction,
    isIntegrationMode,
    checkAuthentication,
    control,
  }
}

export const WebviewProps: React.FunctionComponent<{
  events: BridgeEvents
  actions: BridgeActions
}> = function () {} as any

export const useWebviewAuthentication = () => {
  const { integrationMode, getJWTToken, checkAuthentication } =
    useWebviewIntegration()
  useEffect(() => {
    if (integrationMode) checkAuthentication()
  }, [])
}

export const useExitAction = () => {
  const { updateControl, on, exitWebview } = useWebviewIntegration()
  useEffect(() => {
    updateControl({ position: 'RIGHT', active: true })
  }, [])
  on(
    'onExit',
    () => {
      exitWebview()
    },
    []
  )
}
